import React, { ReactElement, useMemo } from "react";
import { MdNavigateNext } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import { ICON_SIZES } from "../../helpers/constants";
import useMobile from "src/hooks/useMobile";
import { AnimatePresence, motion } from "framer-motion";

export interface IPageTitleArraySubtitle {
  text: string;
  link?: string;
}

interface IPageTitle {
  title: string;
  titleLink?: string;
  subtitle?: string | ReactElement | null | any;
  rightSide?: ReactElement;
  isLoading?: boolean;
  icon?: any;
}

const PageTitle: React.FC<IPageTitle> = ({
  title,
  titleLink = null,
  subtitle,
  rightSide = null,
  isLoading = false,
  icon = null,
}) => {
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const isArraySubtitle = useMemo(() => {
    return Array.isArray(subtitle);
  }, [subtitle]);

  const listItemVariants = {
    hidden: { opacity: 0, x: -50 }, // Inicia desplazado a la derecha
    visible: (index: number) => ({
      opacity: 1,
      x: 0,
      transition: { delay: index * 0.3, duration: 0.5, ease: "easeOut" }, // Delay escalonado
    }),
  };

  return (
    <AnimatePresence mode="wait">
      <div
        className={clsx([
          "flex",
          "flex-col",
          "gap-4",
          "md:w-full",
          "pt-[3px] pb-[7px]",
          "md:py-3",
          // "bg-[#0e223f]",
          // "bg-gray-100",
          "bg-white",
          "md:bg-white",
          "dark:bg-gray-900",
          "dark:md:bg-gray-800",
          "!fixed",
          "md:left-auto",
          "md:right-auto",
          "left-0",
          "right-0",
          "md:!sticky",
          // "top-[66px]",
          "top-[calc(64px+env(safe-area-inset-top))]",
          "md:top-[calc(0px+env(safe-area-inset-top))]",
          "z-20",
          "md:w-[calc(100%+32px)]",
          "md:min-w-[calc(100%+32px)]",
          "md:min-w-full",
          "md:w-auto",
          "px-4",
          "md:px-0",
          // "shadow-md",
          "!shadow-[0px_0px_15px_0px_rgba(0,_0,_0,_0.2)]",
          "md:!shadow-none",
          "mb-4",
          "md:mb-0",
          "relative",
          "md:after:content-['']",
          "md:after:h-[20px]",
          "md:after:-bottom-[20px]",
          "md:after:absolute",
          "md:after:left-0",
          "md:after:right-0",
          "md:after:bg-gradient-to-b",
          "md:after:dark:from-gray-900",
          "md:after:dark:md:from-gray-800",
          "md:after:from-gray-100",
          "md:after:md:from-white",
          "md:mb-2",
          "md:after:to-transparent",
        ])}
      >
        <div
          className={clsx([
            "flex",
            "w-full",
            "flex-row",
            "md:flex-row",
            "items-center",
            "gap-2",
            "md:gap-2",
            "min-h-[50px]",
            "md:min-h-[50px]",
            { "md:justify-between": rightSide },
            "md:pr-0",
            "md:pl-0",
          ])}
        >
          <motion.div
            className={clsx([
              "flex",
              "flex-col",
              "w-full",
              "gap-0",
              "md:flex-row",
              "items-start",
              "md:items-center",
              "text-sm",
              "md:text-base",
              // "md:uppercase",
              "font-medium",
              "text-red-500",
              "dark:text-cyan-400",
            ])}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            {icon && (
              <motion.span
                className={clsx(["hidden", "md:inline-flex", "w-[40px]"])}
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                {icon}
              </motion.span>
            )}
            <h1
              className={clsx([
                "text-[#0e223f]",
                "dark:text-white",
                "md:text-gray-800",
                "dark:md:text-white",
                "border-b-2",
                "border-transparent",
                "leading-none",
                "md:leading-normal",
                "md:font-medium",
                "shrink-0",
                "relative",
                { "after-animated-underline": titleLink },
              ])}
              onClick={() => {
                if (titleLink) {
                  navigate(titleLink);
                }
              }}
            >
              {title}
            </h1>

            {subtitle && (
              <>
                {isArraySubtitle ? (
                  <motion.div
                    className={clsx(["flex", "flex-row"])}
                    initial={{
                      x: isMobile ? -40 : 80,
                    }}
                    exit={{
                      x: isMobile ? -40 : 80,
                    }}
                    animate={{
                      x: 0,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 17,
                    }}
                  >
                    {subtitle?.map?.(
                      (content: IPageTitleArraySubtitle, index: number) => (
                        <div
                          key={index}
                          className={clsx([
                            "flex",
                            "flex-row",
                            "md:flex-row",
                            "items-center",
                            "gap-0",
                            "items-center",
                            "text-base",
                            "font-medium",
                            "w-full",
                            "md:w-auto",
                            "text-red-500",
                            "dark:text-cyan-400",
                            "dark:md:text-cyan-500",
                          ])}
                          onClick={() => {
                            if (content?.link) {
                              navigate(content.link);
                            }
                          }}
                        >
                          {isMobile ? (
                            <>
                              {index !== 0 && (
                                <span className={clsx(["inline-block"])}>
                                  <MdNavigateNext
                                    size={
                                      isMobile ? ICON_SIZES.XS : ICON_SIZES.MD
                                    }
                                  />
                                </span>
                              )}
                            </>
                          ) : (
                            <span className={clsx(["inline-block"])}>
                              <MdNavigateNext
                                size={isMobile ? ICON_SIZES.XS : ICON_SIZES.MD}
                              />
                            </span>
                          )}
                          <h2
                            className={clsx([
                              "flex",
                              "truncate",
                              "font-normal",
                              "text-sm",
                              "md:text-base",
                              {
                                "md:dark:text-white":
                                  index === 0 && content?.link,
                                "after-animated-underline": content?.link,
                              },
                            ])}
                          >
                            {content?.text}
                          </h2>
                        </div>
                      )
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    className={clsx([
                      "flex",
                      "flex-row",
                      "md:flex-row",
                      "items-center",
                      "gap-0",
                      "items-center",
                      "text-base",
                      "font-medium",
                      "w-full",
                      "md:w-auto",
                      "text-red-500",
                      "dark:text-cyan-400",
                      "dark:md:text-cyan-500",
                    ])}
                    initial={{
                      x: isMobile ? -20 : 20,
                    }}
                    exit={{
                      x: isMobile ? -20 : 20,
                    }}
                    animate={{
                      x: 0,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 17,
                    }}
                  >
                    <span className={clsx(["hidden", "md:inline-block"])}>
                      <MdNavigateNext size={ICON_SIZES.MD} />
                    </span>
                    <h2
                      className={clsx([
                        "flex",
                        "truncate",
                        "max-w-[90%]",
                        "font-normal",
                        "text-sm",
                        "md:text-base",
                      ])}
                    >
                      {subtitle}
                    </h2>
                  </motion.div>
                )}
              </>
            )}
          </motion.div>
          {rightSide && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              {rightSide}
            </motion.div>
          )}
        </div>
      </div>
    </AnimatePresence>
  );
};

export default React.memo(PageTitle);
